import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// project imports
import usePermissions from 'hooks/usePermissions';
import { hasAnyAppManagementAccess } from 'utils/appManagementAccess';

// ==============================|| APP MANAGEMENT GUARD ||============================== //

/**
 * Route guard for Application Management pages. Redirects away unless the user can reach the
 * section at all (`manage:application`) and, for a narrower sub-route, also holds one of the
 * `requiredPermissions` passed in. The section check applies either way — a sub-route's own
 * permissions are a narrowing, never a way in.
 */
export default function AppManagementGuard({
  children,
  requiredPermissions
}: {
  children: ReactElement | null;
  requiredPermissions?: string[];
}) {
  const { hasPermission, loading } = usePermissions();
  const navigate = useNavigate();

  const authorized =
    hasAnyAppManagementAccess(hasPermission) && (requiredPermissions ? requiredPermissions.some((code) => hasPermission(code)) : true);

  useEffect(() => {
    if (loading) return;
    if (!authorized) navigate('/pages/access-denied', { replace: true });
  }, [loading, authorized, navigate]);

  if (loading || !authorized) return null;

  return children;
}
